/**
 * Names like the ones you like: the deck that learns.
 *
 * Each like (or love) brings in the names closest to it in style, from its detail file (`like`,
 * worked out by scripts/similar.py). Now and then the name your likes point to most is dealt a few
 * cards ahead, with the name that suggested it on the card. Nothing is saved: it's worked out
 * again from your likes when the app opens.
 */
import { createStore } from "./store";
import { decisions, filters, settings } from "./app";
import { deck, settled } from "./deck";
import { addNeighbours, weavePick, type Pick } from "./taste-core";
import { loadDetail } from "../data/load";
import { sexIdxFor } from "../data/format";
import type { CoreData, NameDetailData } from "../data/types";

/** likes counted so far (key -> weight), for the country loaded */
const counted = new Map<string, number>();
const picks = new Map<string, Pick>();
let forCore: CoreData | null = null;
/** cards decided since a name was last brought forward */
let sinceWoven = 0;

/** where the detail files come from */
export const tasteDeps = { loadDetail };

/** Names dealt early because of a like: key -> the liked name's key. */
export const tasteReasons = createStore<{ because: Record<string, string> }>({ because: {} });

/** The names like a name, for the sex it's in the deck as. */
export function likeList(core: CoreData, d: NameDetailData | null, key: string): string[] {
  const e = core.byKey.get(key);
  if (!d || !e) return [];
  const sx = sexIdxFor(e, settings.get().sex) === 0 ? "F" : "M";
  return d[sx]?.like ?? d[sx === "F" ? "M" : "F"]?.like ?? [];
}

async function count(core: CoreData, key: string, weight: number): Promise<void> {
  const had = counted.get(key) ?? 0;
  if (had >= weight) return;
  counted.set(key, weight);
  const d = await tasteDeps.loadDetail(key).catch(() => null);
  if (core === forCore) addNeighbours(picks, key, likeList(core, d, key), weight - had);
}

/** Start again for a newly loaded country, from the most recent likes. */
export async function seedTaste(core: CoreData): Promise<void> {
  forCore = core;
  counted.clear();
  picks.clear();
  sinceWoven = 0;
  tasteReasons.set({ because: {} });
  const map = decisions.get().map;
  const liked = Object.keys(map)
    .filter((k) => map[k].d > 0 && core.byKey.has(k))
    .sort((a, b) => map[b].t - map[a].t)
    .slice(0, 40);
  await Promise.all(liked.map((k) => count(core, k, map[k].d === 2 ? 2 : 1)));
}

/** After a decision in the deck: learn from a like, and now and then bring a name forward. */
export function tasteAfter(core: CoreData, key: string, verdict: number): void {
  if (core !== forCore) return;
  sinceWoven++;
  if (verdict > 0) void count(core, key, verdict === 2 ? 2 : 1).then(() => weave(core));
}

function weave(core: CoreData): void {
  const f = filters.get();
  // only in the open deck (not a collection, list or search someone chose), after a few likes,
  // and at most one name brought forward every three cards
  if (core !== forCore || settings.get().tastePicks === false || f.sort !== "smart" || f.collection || f.list) return;
  if (counted.size < 3 || sinceWoven < 3) return;
  const { order, front } = deck.get();
  const pick = weavePick(core, order, front, (k) => settled(core, k), picks);
  if (!pick) return;
  deck.set({ order: pick.order });
  tasteReasons.set((s) => ({ because: { ...s.because, [pick.key]: pick.because } }));
  sinceWoven = 0;
}
